import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import RecapItem from 'components/Content/Recap/RecapItem';
import { Grid, Button, Box } from 'components/ui';
import RecapImage from 'components/Content/Recap/RecapImage';
import Markdown from 'components/Markdown/Markdown';
import RichText from 'components/Content/RichText';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import MediaUtils from 'utils/Media';
import resourceWrapper from 'utils/framework/resourceWrapper';
import RecapBasketEN from 'components/Content/Recap/RecapBasket/locales/RecapBasket_en_US';
import RecapBasketFR from 'components/Content/Recap/RecapBasket/locales/RecapBasket_fr_CA';
import {
    radii, colors, fontSizes, space, lineHeights
} from 'style/config';
import uiUtils from 'utils/UI';
import RtcoOfferDetailsModal from 'components/Content/Recap/RecapBasket/RtcoOfferDetailsModal';

const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const { Media } = MediaUtils;
const getText = resourceWrapper(getLocalisedDataFromResource(RecapBasketEN, RecapBasketFR));

// Per Figma (PUWYLO Optimizations): SKU tiles grouped left with a 4px gap. Kept in sync with the
// sibling recap cards (RecapGrid) at [59, 79] so the layout width matches the requested image asset
// per breakpoint and the RTCO card stays visually consistent with the rest of the carousel.
const IMAGE_SIZE = [59, 79];

const PROMO_ARROW_SUFFIX = '\u00A0\u00A0\u23F5';

function appendPromoArrowToRichText(text) {
    if (!text?.json?.content) {
        return text;
    }

    const { content } = text.json;
    const lastParagraphIndex = content.findLastIndex(node => node.nodeType === 'paragraph');

    if (lastParagraphIndex === -1) {
        return text;
    }

    const lastParagraph = content[lastParagraphIndex];
    const updatedParagraph = {
        ...lastParagraph,
        content: [
            ...(Array.isArray(lastParagraph.content) ? lastParagraph.content : []),
            {
                data: {},
                marks: [],
                value: PROMO_ARROW_SUFFIX,
                nodeType: 'text'
            }
        ]
    };

    return {
        ...text,
        json: {
            ...text.json,
            content: content.map((node, index) => (index === lastParagraphIndex ? updatedParagraph : node))
        }
    };
}

function getCheckoutMessage({ hasCheckoutEligibleItems, hasStandardItems, hasSufficientPoints, isBopisOnly }) {
    if (!hasCheckoutEligibleItems) {
        return null;
    }

    if (hasSufficientPoints) {
        return getText('applyPoints');
    }

    return hasStandardItems && !isBopisOnly ? getText('freeShipping') : null;
}

// Keep CMS colors on hover too; null falls back to the default hover in styles.promoContainer.
// When `isInteractive` is false (no promoCode) the promo is not clickable, so we drop the hover state.
function buildPromoThemeCss(banner, isInteractive = true) {
    if (!banner?.backgroundColor && !banner?.color) {
        return null;
    }

    const themeColors = {
        ...(banner.backgroundColor && { backgroundColor: banner.backgroundColor }),
        ...(banner.color && { color: banner.color })
    };

    return isInteractive ? { ...themeColors, '&:hover': themeColors } : themeColors;
}

/**
 * Recap carousel basket card, including optional RTCO promo banner and offer-details modal.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
function RecapBasket({ uniqueSkus, totalItemCount, shouldRenderCarouselBasket, ...props }) {
    const [isOfferDetailsModalOpen, setIsOfferDetailsModalOpen] = useState(false);

    const checkoutMessage = getCheckoutMessage(props);

    const { rtcoPromoData, rtcoPromoLoading } = props;

    const isControlGroup = rtcoPromoData?.isControl && rtcoPromoData?.isAbTest;
    const shouldShowPromo = rtcoPromoData && !isControlGroup && rtcoPromoData.isRtcoEnabled;
    // Without a promoCode (e.g. Loyalty promos) there is nothing to apply, so the promo is shown as a
    // static message: no arrow affordance, not clickable, and the offer-details modal never opens.
    const hasPromoCode = Boolean(rtcoPromoData?.promoCode);

    const handlePromoClick = event => {
        event.stopPropagation();
        setIsOfferDetailsModalOpen(true);
    };

    // Without a promoCode the promo is informational only: swallow the click so it neither opens the
    // modal nor bubbles to the RecapItem card wrapper (which would navigate to the basket).
    const handleStaticPromoClick = event => {
        event.stopPropagation();
    };

    const renderPromo = () => {
        if (rtcoPromoLoading && !rtcoPromoData) {
            return (
                <Box
                    css={styles.promoPlaceholder}
                    height={40}
                    borderRadius={radii[2]}
                    data-testid='skeleton'
                    aria-busy='true'
                />
            );
        }

        if (shouldShowPromo) {
            const { banner } = rtcoPromoData;
            const text = banner?.text ? (hasPromoCode ? appendPromoArrowToRichText(banner.text) : banner.text) : null;
            const promoThemeCss = buildPromoThemeCss(banner, hasPromoCode);

            const promoContent = text && (
                <RichText
                    content={text}
                    style={styles.promoText}
                />
            );

            if (!hasPromoCode) {
                // Non-interactive: a plain div (never a <button>) that swallows the click so the
                // informational promo is not clickable — it opens no modal and does not bubble to the
                // RecapItem card wrapper (which would navigate to the basket).
                return (
                    <div
                        css={[styles.promoContainerStatic, promoThemeCss]}
                        onClick={handleStaticPromoClick}
                    >
                        {promoContent}
                    </div>
                );
            }

            return (
                <Button
                    variant='secondary'
                    css={[styles.promoContainer, promoThemeCss]}
                    onClick={handlePromoClick}
                >
                    {promoContent}
                </Button>
            );
        }

        return null;
    };

    return (
        <Fragment>
            <RecapItem
                {...props}
                // out of many items, only 1 will be rtco and only 1 will have this key
                isRtcoItem={Boolean('rtcoPromoData' in props)}
                wrapperIs='div'
            >
                {shouldRenderCarouselBasket && (
                    <Fragment>
                        {renderPromo()}
                        {checkoutMessage && (
                            <Fragment>
                                <Media lessThan='md'>
                                    {LanguageLocaleUtils.isUS() && (
                                        <Markdown
                                            css={styles.pill}
                                            content={checkoutMessage}
                                        />
                                    )}
                                </Media>
                                <Media greaterThan='sm'>
                                    {LanguageLocaleUtils.isCanada() && (
                                        <Markdown
                                            css={styles.pill}
                                            content={checkoutMessage}
                                        />
                                    )}
                                </Media>
                            </Fragment>
                        )}
                        <Grid
                            columns={[`repeat(2, minmax(0, ${IMAGE_SIZE[0]}px))`, `repeat(2, minmax(0, ${IMAGE_SIZE[1]}px))`]}
                            gap={1}
                            css={styles.productGrid}
                        >
                            {uniqueSkus.slice(0, 2).map(sku => (
                                <RecapImage
                                    key={`recap_basket_${sku.skuId}`}
                                    sku={sku}
                                    size={IMAGE_SIZE}
                                />
                            ))}
                        </Grid>
                        <Button
                            is='span'
                            variant='special'
                            size='sm'
                            css={styles.checkoutButton}
                            children={getText('checkoutWithCount', false, String(totalItemCount))}
                        />
                    </Fragment>
                )}
            </RecapItem>
            {rtcoPromoData && hasPromoCode && (
                <RtcoOfferDetailsModal
                    isOpen={isOfferDetailsModalOpen && Boolean(shouldShowPromo && rtcoPromoData)}
                    onDismiss={() => setIsOfferDetailsModalOpen(false)}
                    rtcoPromoData={rtcoPromoData}
                />
            )}
        </Fragment>
    );
}

const styles = {
    productGrid: {
        marginTop: space[4],
        marginBottom: space[5]
    },
    checkoutButton: {
        marginTop: 'auto',
        marginBottom: space[1]
    },
    pill: {
        display: 'inline-block',
        padding: space[1],
        marginRight: 'auto',
        marginBottom: space[3],
        borderRadius: radii[2],
        backgroundColor: colors.lightBlue,
        fontSize: fontSizes.sm
    },
    promoPlaceholder: {
        width: '100%',
        marginBottom: space[3],
        ...uiUtils.SKELETON_ANIMATION
    },
    promoContainer: {
        height: 'auto',
        minHeight: 'auto',
        padding: space[1],
        marginBottom: space[1],
        borderRadius: radii[2],
        backgroundColor: colors.lightBlue,
        fontWeight: 'var(--font-weight-normal)',
        cursor: 'pointer',
        border: 'none',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: colors.blue,
            color: colors.white
        }
    },
    promoContainerStatic: {
        display: 'flex',
        padding: space[1],
        marginBottom: space[1],
        borderRadius: radii[2],
        backgroundColor: colors.lightBlue,
        fontWeight: 'var(--font-weight-normal)'
    },
    promoText: {
        alignSelf: 'start',
        fontSize: fontSizes.xs,
        lineHeight: lineHeights.tight,
        textAlign: 'start'
    }
};

RecapBasket.propTypes = {
    uniqueSkus: PropTypes.array.isRequired,
    totalItemCount: PropTypes.number.isRequired,
    shouldRenderCarouselBasket: PropTypes.bool.isRequired,
    rtcoPromoData: PropTypes.shape({
        context: PropTypes.string,
        variation: PropTypes.string,
        promoType: PropTypes.string,
        promoCode: PropTypes.string,
        isControl: PropTypes.bool,
        isDefault: PropTypes.bool,
        isAbTest: PropTypes.bool,
        isRtcoEnabled: PropTypes.bool,
        isRouge: PropTypes.bool,
        banner: PropTypes.shape({
            text: PropTypes.object,
            sid: PropTypes.string,
            mediaPlacement: PropTypes.string
        }),
        legalText: PropTypes.string,
        disclaimer: PropTypes.shape({
            type: PropTypes.string,
            sid: PropTypes.string,
            content: PropTypes.object
        }),
        modalSubtitle: PropTypes.string,
        modalEligibility: PropTypes.string
    }),
    rtcoPromoLoading: PropTypes.bool
};

export default wrapFunctionalComponent(RecapBasket, 'RecapBasket');
